import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { PageHero } from '../layout/PageHero';
import { TopicIndex } from '../layout/TopicIndex';
import { StatementSection } from '../layout/StatementSection';
import { FutureUses } from '../layout/FutureUses';
import { DomainIcon } from '../demoCenter/DomainIcon';
import { DemoStatusBadge } from '../demoCenter/DemoStatusBadge';
import type { ApplicationPageContent } from '../../data/applicationPages';
import { demoUrlFor, getDemoDomain, type DemoDomain } from '../../data/demoCenter';
import { assessmentResearchStatus, demoAvailability } from '../../data/siteContent';
import { fadeUp } from '../../utils/motion';
const workflowColumns: Record<number, string> = {
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6'
};
type ApplicationPageProps = {
  content: ApplicationPageContent;
  /** Replaces the default hero; the page's own intro is then shown below it. */
  hero?: React.ReactNode;
  /** Sections inserted after the demo and workflow (e.g. Government workspaces). */
  afterDemo?: React.ReactNode;
  demoDomainId: DemoDomain['id'];
};

/**
 * Shared sector-page structure: purpose → demo and scope → workflow →
 * supporting architecture → proposed production uses → status → pilot contact.
 */
export function ApplicationPage({
  content,
  hero,
  afterDemo,
  demoDomainId
}: ApplicationPageProps) {
  const {
    highlights
  } = content;
  const domain = getDemoDomain(demoDomainId);
  return <>
      {hero ?? <PageHero eyebrow={domain?.solutionLabel ?? 'Solutions'} title={content.title} lead={content.lead} body={content.intro} backLink={{
      href: '/solutions',
      label: 'All solutions'
    }} data-id="element-93" data-mp="117zz0qervmai" />}

      {domain && <section aria-labelledby="demo-scope-title" className="bg-surface-bg border-b border-line py-16 lg:py-20" data-id="element-94" data-mp="1ow9ezbpb1ftq">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-95" data-mp="1k3g9umpxc5h4">
            <motion.div {...fadeUp} className="lg:col-span-5" data-id="element-96" data-mp="233ce3hle39j4">
              <div className="flex items-center gap-3" data-id="element-97" data-mp="27yrscanb77g2">
                <DomainIcon id={domain.id} data-id="element-98" data-mp="1u8xzzicli51l" />
                {domain.status === 'scoped' && <DemoStatusBadge status={domain.status} data-id="element-99" data-mp="1u6vdh8aniu1m" />}
              </div>
              <h2 id="demo-scope-title" className="mt-4 font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-100" data-mp="1y8gm2hg5km60">
                The demo and its scope
              </h2>
              <p className="mt-2 text-sm text-ink-muted" data-id="element-101" data-mp="1pb6bkyb5t125">{domain.workspaces.join(' · ')}</p>
              <a href={demoUrlFor(domain.id)} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-5 py-3 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-102" data-mp="1bb58dc7f9hji">
                {domain.actionLabel}
                <ArrowUpRight size={18} aria-hidden="true" data-id="element-103" data-mp="y34nt24ffltr" />
                <span className="sr-only" data-id="element-104" data-mp="u0rlyossw829">(opens in a new tab)</span>
              </a>
            </motion.div>
            <motion.div {...fadeUp} className="lg:col-span-7 lg:pt-12 space-y-4" data-id="element-105" data-mp="17sfwinneioh4">
              <p className="text-xl text-ink-dark leading-relaxed" data-id="element-106" data-mp="1316b5z8vy7b1">{content.demoScope}</p>
              <p className="text-ink-muted leading-relaxed" data-id="element-107" data-mp="12zczu6nbuywp">{demoAvailability}</p>
            </motion.div>

            {domain.workflow.length > 0 && <motion.div {...fadeUp} className="lg:col-span-12 mt-4" data-id="element-108" data-mp="1cq7n9ecb84jq">
                <h3 className="text-sm font-medium text-ink-muted mb-4" data-id="element-109" data-mp="1qraavviydmwr">The workflow in the demo</h3>
                <ol className={`grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line rounded-lg overflow-hidden ${workflowColumns[domain.workflow.length] ?? 'lg:grid-cols-4'}`} data-id="element-110" data-mp="1lr5wcb1lv296">
                  {domain.workflow.map((step, index) => <li key={step} className="flex items-baseline gap-3 bg-white p-5" data-id="element-111" data-mp="3pwxknr9448vp" data-mpi={index}>
                      <span className="font-heading font-bold text-sm text-teal" data-id="element-112" data-mp="3ty9cm3fgl672">{index + 1}</span>
                      <span className="font-heading font-semibold text-ink-dark" data-id="element-113" data-mp="2u1531at8gp7">{step}</span>
                    </li>)}
                </ol>
              </motion.div>}
          </div>
        </section>}

      {highlights && <section className="bg-white border-b border-line py-14 lg:py-16" data-id="element-114" data-mp="3b8t0eiuevpat">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-115" data-mp="3b6qarzx67y6m">
            <motion.h2 {...fadeUp} className="font-heading font-bold text-2xl text-ink-dark mb-6" data-id="element-116" data-mp="3g748op525rqx">
              {highlights.title}
            </motion.h2>
            <motion.ul {...fadeUp} className={`grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line rounded-lg overflow-hidden ${highlights.items.length > 3 ? 'lg:grid-cols-6' : 'md:grid-cols-3'}`} data-id="element-117" data-mp="2nj8hfzy9cs1j">
              {highlights.items.map(item => <li key={item.title} className="bg-white p-6" data-id="element-118" data-mp="2g0r1pbzsgh04">
                  <p className="font-heading font-bold text-ink-dark leading-snug" data-id="element-119" data-mp="2b0crbnapsuyx">{item.title}</p>
                  {item.description && <p className="mt-2 text-ink-muted leading-relaxed" data-id="element-120" data-mp="262b9f5p0z1p1">{item.description}</p>}
                </li>)}
            </motion.ul>
            {highlights.note && <p className="mt-6 max-w-4xl text-ink-muted leading-relaxed" data-id="element-121" data-mp="1x980napbrd0q">{highlights.note}</p>}
          </div>
        </section>}

      {afterDemo}

      <section aria-labelledby="architecture-title" className="bg-white border-b border-line py-16 lg:py-20" data-id="element-122" data-mp="1sawminrzygcp">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16" data-id="element-123" data-mp="1ncv6kbpvax5g">
          <h2 id="architecture-title" className="lg:col-span-5 font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-124" data-mp="1eac3bqi66iio">
            Supporting architecture
          </h2>
          <p className="lg:col-span-7 text-lg text-ink-dark leading-relaxed" data-id="element-125" data-mp="13rl4w4wszq7z">{content.architecture}</p>
        </motion.div>
      </section>

      <TopicIndex topics={content.topics} indexLabel="On this page" heading={content.topicsHeading} headingNote="These describe intended production uses. They have not been deployed." data-id="element-126" data-mp="upbv5h9d00vn" />

      <section aria-labelledby="status-title" className="bg-white border-t border-line py-16 lg:py-20" data-id="element-127" data-mp="1ndr9s2okwh6g">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-128" data-mp="1re1g5gga99lu">
          <motion.div {...fadeUp} className="lg:col-span-5" data-id="element-129" data-mp="1dlbsl7l3vwmm">
            <h2 id="status-title" className="font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-130" data-mp="1ilhfybkcewyw">
              Validation and development status
            </h2>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-7 space-y-4" data-id="element-131" data-mp="2b3lkadlfdtnj">
            <p className="text-lg text-ink-dark leading-relaxed" data-id="element-132" data-mp="2kgu5xxsi04ma">{content.status}</p>
            <p className="text-ink-muted leading-relaxed" data-id="element-133" data-mp="2ggabx3381h4d">{assessmentResearchStatus}</p>
            {content.futureUseArea && <FutureUses area={content.futureUseArea} data-id="element-134" data-mp="2u7ort11u1277" />}
          </motion.div>
        </div>
      </section>

      <StatementSection title={content.why.title} body={content.why.body} data-id="element-135" data-mp="2p9diqsjd2vq1">
        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-id="element-136" data-mp="21ihxyfdbp0sa">
          <a href="/contact" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-137" data-mp="21kuaue484kyh">
            Discuss a pilot
            <ArrowRight size={16} aria-hidden="true" data-id="element-138" data-mp="26i464aeanqu8" />
          </a>
          <a href="/solutions" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-139" data-mp="26jxi857of39m">
            Explore other solutions
            <ArrowRight size={16} aria-hidden="true" data-id="element-140" data-mp="1g8ditipm713v" />
          </a>
        </div>
      </StatementSection>
    </>;
}